import React, { useState } from "react";
import ReportPageShell from "../components/ReportPageShell";
import MetricGrid from "../components/MetricGrid";
import StatCard from "../components/StatCard";
import { SecondaryButton } from "../../../components/Button";
import { useVerticalSales } from "../shared/useVerticalSales";
import { useSearchGate } from "../shared/useSearchGate";
import { defaultFilters } from "../shared/filters";
import { centsToUsd, formatMetric, METRIC_LABELS } from "../shared/format";
import { hasUntenderedMetric } from "../shared/tenderBreakdown";
import { UNTENDERED_TENDER_ONLY_NOTE } from "../shared/metricDefinitions";
import { MetricKey } from "../api/types";

const FINANCIAL_CARDS: MetricKey[] = [
  "NetSales",
  "Gross",
  "Tax",
  "Discount",
  "Refunds",
  "Tip",
  "Surcharge",
  "OrderCount",
  "TransactionCount",
];

const TOOLTIP =
  "Event-level Vertical Sales — one stacked view for the selected event: the financial summary (incl. Net), " +
  "the payment breakdown (per-tender GROSS sales — excludes tips and the service fee, so it reconciles to " +
  "Gross Sales, like the old report), the card-brand breakdown (money collected by card brand), and the " +
  "product mix. Optionally narrow to a single vendor (like the old vendor-scoped report). Export to PDF " +
  "mirrors the old report's document. Net Sales is the real-money net that matches the Financial reports.";

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="mb-4 rounded-md bg-white p-4 shadow-sm">
    <h3 className="mb-2 text-sm font-bold uppercase tracking-wide text-gray-700">{title}</h3>
    {children}
  </div>
);

// Event-scoped Vertical Sales page (Marc's stacked layout). Requires a single event; the vendor
// filter narrows all four underlying reports to that vendor (the old report was vendor-scoped).
export default function VerticalSalesReport() {
  const [filters, setFilters] = useState(defaultFilters);
  const { submitted, isDirty, hasRun, search } = useSearchGate({ filters });
  const vs = useVerticalSales(submitted ? submitted.filters : null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  // PDF is built from the four loaded responses — the exact data on screen. The renderer module
  // is imported on demand so @react-pdf/renderer doesn't weigh down the route chunk.
  const handleExportPdf = async () => {
    if (!submitted || !vs.financial) return;
    setExporting(true);
    setExportError(null);
    try {
      const { exportVerticalSalesPdf } = await import("../components/VerticalSalesPdf");
      await exportVerticalSalesPdf({
        eventName: submitted.filters.eventName || "Event",
        vendorName: submitted.filters.vendorName ?? "",
        window: vs.financial.window,
        financial: vs.financial,
        paymentBreakdown: vs.paymentBreakdown,
        // So the Payment Breakdown's bold 100.0% is qualified when it totals short of Gross.
        untendered: vs.financial.totals.TenderUntenderedAmount ?? 0,
        gross: vs.financial.totals.Gross ?? 0,
        cardBrand: vs.cardBrand,
        productMix: vs.productMix,
      });
    } catch (e) {
      setExportError(e instanceof Error ? e.message : "PDF export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <ReportPageShell
      title="Vertical Sales"
      tooltipText={TOOLTIP}
      filters={filters}
      onFiltersChange={setFilters}
      state={{ data: vs.financial, loading: vs.loading, error: vs.error }}
      idlePrompt="Select a single event (and optionally a vendor) to build the Vertical Sales breakdown."
      onSearch={search}
      isDirty={isDirty}
      hasRun={hasRun}
      filterVendor
      actions={
        <>
          <SecondaryButton
            onClick={handleExportPdf}
            isDisabled={!vs.financial || vs.loading || exporting}
          >
            {exporting ? "Exporting…" : "Export PDF"}
          </SecondaryButton>
          {exportError && (
            <span className="text-xs font-medium text-red-600">{exportError}</span>
          )}
        </>
      }
    >
      {(financial) => {
        const untendered = financial.totals.TenderUntenderedAmount ?? 0;
        return (
          <>
            <Section title="Financial summary">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                {FINANCIAL_CARDS.filter((m) => financial.metrics.includes(m)).map((m) => (
                  <StatCard
                    key={m}
                    label={METRIC_LABELS[m]}
                    value={formatMetric(m, financial.totals[m])}
                    emphasis={m === "NetSales"}
                  />
                ))}
              </div>
            </Section>
  
            {/* Capped Tender* figures from the financial call, like the old report — NOT PaymentMix
                (money collected, tips + service fee included). This totals to Σ tender $, which is
                the Gross card above MINUS Untendered: Untendered is not a tender class and must not
                become a payment row here, so the gap is explained in the footnote (RP-2155/2159). */}
            {vs.paymentBreakdown && (
              <Section title="Payment breakdown (tender sales)">
                <MetricGrid
                  data={vs.paymentBreakdown}
                  keyColumnHeader="Tender"
                  exportName="VerticalSales_PaymentBreakdown"
                  height="300px"
                  csvExtra={hasUntenderedMetric(financial) ? [`NOTE: ${UNTENDERED_TENDER_ONLY_NOTE}`] : undefined}
                />
                {hasUntenderedMetric(financial) && (
                  <p className="mt-2 text-xs text-gray-500">
                    <span className="font-semibold">Note:</span> {UNTENDERED_TENDER_ONLY_NOTE}
                    {untendered > 0 &&
                      ` Untendered for this scope is ${centsToUsd(untendered)} of the ${centsToUsd(
                        financial.totals.Gross
                      )} Gross Sales above.`}
                  </p>
                )}
              </Section>
            )}
  
            {vs.cardBrand && (
              <Section title="Card brand breakdown">
                <MetricGrid
                  data={vs.cardBrand}
                  keyColumnHeader="Card Brand"
                  exportName="VerticalSales_CardBrand"
                  height="260px"
                />
              </Section>
            )}
  
            {vs.productMix && (
              <Section title="Product mix">
                <MetricGrid
                  data={vs.productMix}
                  keyColumnHeader="Item"
                  groupColumnHeader="Category"
                  exportName="VerticalSales_ProductMix"
                  height="440px"
                />
              </Section>
            )}
          </>
        );
      }}
    </ReportPageShell>
  );
}
